import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, ShieldCheck, KeyRound, Ban, Trash2, Pencil, CheckCircle2, Crown, ScrollText, Eye, Edit3, Download } from "lucide-react";
import { AdminTabs } from "@/components/AdminTabs";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const fmt = (s) => (s ? new Date(s).toLocaleDateString() : "—");
const fmtDT = (s) => (s ? new Date(s).toLocaleString() : "—");
const LEVEL_STYLES = {
  none: "bg-[#8A9A86]/15 text-[#5C6B64]",
  view: "bg-[#CC5A3A]/12 text-[#CC5A3A]",
  full: "bg-[#2D6A4F]/12 text-[#2D6A4F]",
};
const LEVEL_LABEL = { none: "No access", view: "View only", full: "Full access" };

const LevelPicker = ({ value, onChange, testid }) => (
  <div className="inline-flex rounded-lg border border-[#1E3F33]/15 overflow-hidden" data-testid={testid}>
    {["none", "view", "full"].map((lvl) => (
      <button
        key={lvl}
        type="button"
        onClick={() => onChange(lvl)}
        data-testid={`${testid}-${lvl}`}
        className={`px-2.5 py-1 text-xs font-medium transition-colors ${
          value === lvl ? (lvl === "full" ? "bg-[#2D6A4F] text-white" : lvl === "view" ? "bg-[#CC5A3A] text-white" : "bg-[#5C6B64] text-white") : "bg-white text-[#5C6B64] hover:bg-[#F0EFEA]"
        }`}
      >
        {LEVEL_LABEL[lvl]}
      </button>
    ))}
  </div>
);

const PermissionEditor = ({ features, perms, setPerms }) => (
  <div className="grid sm:grid-cols-2 gap-2 max-h-[45vh] overflow-y-auto pr-1" data-testid="permission-editor">
    {features.map((f) => (
      <div key={f.key} className="flex items-center justify-between gap-2 rounded-lg border border-[#1E3F33]/10 px-3 py-2">
        <span className="text-sm text-[#1E3F33]">{f.label}</span>
        <LevelPicker value={perms[f.key] || "none"} onChange={(v) => setPerms({ ...perms, [f.key]: v })} testid={`perm-${f.key}`} />
      </div>
    ))}
  </div>
);

export default function AdminManageAdmins() {
  const [admins, setAdmins] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ username: "", name: "", email: "", password: "" });
  const [formPerms, setFormPerms] = useState({});
  const [saving, setSaving] = useState(false);

  const [editTarget, setEditTarget] = useState(null);
  const [editPerms, setEditPerms] = useState({});
  const [editName, setEditName] = useState("");

  const [pwdTarget, setPwdTarget] = useState(null);
  const [newPwd, setNewPwd] = useState("");

  const [activity, setActivity] = useState([]);
  const [activityFilter, setActivityFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const loadActivity = async (opts = {}) => {
    const actorId = opts.actorId !== undefined ? opts.actorId : activityFilter;
    const action = opts.action !== undefined ? opts.action : actionFilter;
    const start = opts.start !== undefined ? opts.start : startDate;
    const end = opts.end !== undefined ? opts.end : endDate;
    const params = {};
    if (actorId) params.actor_id = actorId;
    if (action) params.action = action;
    if (start) params.start = start;
    if (end) params.end = end;
    try {
      const { data } = await api.get("/admin/admin-activity", { params });
      setActivity(data.items || []);
    } catch (e) {
      /* non-fatal */
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [a, f] = await Promise.all([api.get("/admin/admins"), api.get("/admin/admins/features")]);
      setAdmins(a.data.items || []);
      setFeatures(f.data.features || []);
      loadActivity();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const exportActivity = async () => {
    const params = {};
    if (activityFilter) params.actor_id = activityFilter;
    if (actionFilter) params.action = actionFilter;
    if (startDate) params.start = startDate;
    if (endDate) params.end = endDate;
    try {
      const res = await api.get("/admin/admin-activity/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `admin-activity-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Activity log exported");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const emptyPerms = () => Object.fromEntries(features.map((f) => [f.key, "none"]));

  const openCreate = () => {
    setForm({ username: "", name: "", email: "", password: "" });
    setFormPerms(emptyPerms());
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    setSaving(true);
    try {
      await api.post("/admin/admins", { ...form, permissions: formPerms });
      toast.success(`Admin "${form.username}" created`);
      setCreateOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (a) => {
    setEditTarget(a);
    setEditName(a.name || "");
    setEditPerms({ ...emptyPerms(), ...(a.permissions || {}) });
  };

  const submitEdit = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/admins/${editTarget.id}`, { name: editName, permissions: editPerms });
      toast.success("Permissions updated");
      setEditTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const submitResetPwd = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/admins/${pwdTarget.id}/reset-password`, { new_password: newPwd });
      toast.success(`Password reset for "${pwdTarget.username}"`);
      setPwdTarget(null);
      setNewPwd("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleSuspend = async (a) => {
    if (!window.confirm(`${a.suspended ? "Reactivate" : "Suspend"} admin "${a.username}"?`)) return;
    try {
      await api.patch(`/admin/admins/${a.id}/suspend`, { suspended: !a.suspended });
      toast.success(a.suspended ? "Admin reactivated" : "Admin suspended");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const removeAdmin = async (a) => {
    if (!window.confirm(`Permanently delete admin "${a.username}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/admins/${a.id}`);
      toast.success("Admin deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const permSummary = (a) => {
    if (a.is_super_admin) return "All features · full access";
    const full = features.filter((f) => (a.permissions?.[f.key]) === "full").length;
    const view = features.filter((f) => (a.permissions?.[f.key]) === "view").length;
    if (!full && !view) return "No sections assigned";
    return `${full} full · ${view} view-only`;
  };

  return (
    <div className="container-x py-12" data-testid="admin-manage-admins">
      <AdminTabs />
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#CC5A3A] mb-2">Access control</div>
          <h1 className="font-heading text-3xl text-[#1E3F33] font-semibold flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-[#2D6A4F]" /> Admin accounts
          </h1>
          <p className="text-[#5C6B64] mt-2 max-w-2xl">Create admin logins and control exactly which sections each one can view or change.</p>
        </div>
        <button onClick={openCreate} data-testid="add-admin-btn" className="btn-primary py-2.5 px-4 inline-flex items-center gap-2">
          <UserPlus className="w-4 h-4" /> Add admin
        </button>
      </div>

      {loading ? (
        <div className="text-[#5C6B64]">Loading…</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[#1E3F33]/10">
          <table className="w-full text-sm" data-testid="admins-table">
            <thead className="bg-[#F0EFEA] text-left text-[#1E3F33]"><tr>
              <th className="px-4 py-2.5 font-medium">Username</th>
              <th className="px-4 py-2.5 font-medium">Name</th>
              <th className="px-4 py-2.5 font-medium">Access</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
              <th className="px-4 py-2.5 font-medium">Added</th>
              <th className="px-4 py-2.5"></th>
            </tr></thead>
            <tbody>
              {admins.map((a) => (
                <tr key={a.id} className="border-t border-[#1E3F33]/8" data-testid={`admin-row-${a.username}`}>
                  <td className="px-4 py-2.5 text-[#1E3F33] font-medium">
                    <span className="inline-flex items-center gap-1.5">
                      {a.is_super_admin && <Crown className="w-3.5 h-3.5 text-[#CC5A3A]" title="Main admin" />}
                      {a.username}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-[#5C6B64]">{a.name || "—"}</td>
                  <td className="px-4 py-2.5 text-[#5C6B64]">{permSummary(a)}</td>
                  <td className="px-4 py-2.5">
                    {a.is_super_admin ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-[#CC5A3A]/12 text-[#CC5A3A]">Main admin</span>
                    ) : a.suspended ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-[#E63946]/12 text-[#E63946]">Suspended</span>
                    ) : (
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-[#2D6A4F]/12 text-[#2D6A4F]">Active</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-[#5C6B64] whitespace-nowrap">{fmt(a.created_at)}</td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    {a.is_super_admin ? (
                      <span className="text-xs text-[#8A9A86]">—</span>
                    ) : (
                      <div className="inline-flex items-center gap-3 justify-end">
                        <button onClick={() => openEdit(a)} data-testid={`admin-edit-${a.username}`} className="text-xs inline-flex items-center gap-1 text-[#2D6A4F] hover:underline"><Pencil className="w-3.5 h-3.5" /> Permissions</button>
                        <button onClick={() => setPwdTarget(a)} data-testid={`admin-resetpwd-${a.username}`} className="text-xs inline-flex items-center gap-1 text-[#1E3F33] hover:underline"><KeyRound className="w-3.5 h-3.5" /> Password</button>
                        <button onClick={() => toggleSuspend(a)} data-testid={`admin-suspend-${a.username}`} className="text-xs inline-flex items-center gap-1 text-[#CC5A3A] hover:underline"><Ban className="w-3.5 h-3.5" /> {a.suspended ? "Reactivate" : "Suspend"}</button>
                        <button onClick={() => removeAdmin(a)} data-testid={`admin-delete-${a.username}`} className="text-xs inline-flex items-center gap-1 text-[#E63946] hover:underline"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Activity log */}
      <div className="mt-10" data-testid="admin-activity-log">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-3">
          <h2 className="font-heading text-xl text-[#1E3F33] font-semibold flex items-center gap-2">
            <ScrollText className="w-5 h-5 text-[#2D6A4F]" /> Admin activity log
          </h2>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              data-testid="activity-filter"
              value={activityFilter}
              onChange={(e) => { setActivityFilter(e.target.value); loadActivity({ actorId: e.target.value }); }}
              className="rounded-lg border border-[#1E3F33]/20 px-3 py-1.5 text-sm text-[#1E3F33] bg-white focus:outline-none focus:ring-2 focus:ring-[#2D6A4F]/30"
            >
              <option value="">All sub-admins</option>
              {admins.filter((a) => !a.is_super_admin).map((a) => (
                <option key={a.id} value={a.id}>{a.username}</option>
              ))}
            </select>
            <select
              data-testid="activity-action-filter"
              value={actionFilter}
              onChange={(e) => { setActionFilter(e.target.value); loadActivity({ action: e.target.value }); }}
              className="rounded-lg border border-[#1E3F33]/20 px-3 py-1.5 text-sm text-[#1E3F33] bg-white focus:outline-none focus:ring-2 focus:ring-[#2D6A4F]/30"
            >
              <option value="">All actions</option>
              <option value="view">Viewed</option>
              <option value="change">Changed</option>
            </select>
            <input
              type="date"
              data-testid="activity-start-date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); loadActivity({ start: e.target.value }); }}
              className="rounded-lg border border-[#1E3F33]/20 px-2.5 py-1.5 text-sm text-[#1E3F33] bg-white focus:outline-none focus:ring-2 focus:ring-[#2D6A4F]/30"
            />
            <span className="text-xs text-[#8A9A86]">to</span>
            <input
              type="date"
              data-testid="activity-end-date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); loadActivity({ end: e.target.value }); }}
              className="rounded-lg border border-[#1E3F33]/20 px-2.5 py-1.5 text-sm text-[#1E3F33] bg-white focus:outline-none focus:ring-2 focus:ring-[#2D6A4F]/30"
            />
            {(activityFilter || actionFilter || startDate || endDate) && (
              <button
                data-testid="activity-clear-filters"
                onClick={() => { setActivityFilter(""); setActionFilter(""); setStartDate(""); setEndDate(""); loadActivity({ actorId: "", action: "", start: "", end: "" }); }}
                className="text-xs py-1.5 px-3 rounded-full border border-[#1E3F33]/20 text-[#5C6B64] hover:bg-[#1E3F33]/5"
              >
                Clear
              </button>
            )}
            <button
              data-testid="activity-export-csv"
              onClick={exportActivity}
              className="text-xs py-1.5 px-3 inline-flex items-center gap-1.5 rounded-full border border-[#2D6A4F]/40 text-[#2D6A4F] hover:bg-[#2D6A4F]/10"
            >
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
          </div>
        </div>
        <p className="text-xs text-[#8A9A86] mb-3">What each sub-admin has viewed and changed, plus test-notification sends. The main admin's routine browsing isn't listed here. Entries are kept permanently.</p>
        <div className="overflow-x-auto rounded-xl border border-[#1E3F33]/10">
          {activity.length === 0 ? (
            <p className="px-4 py-6 text-sm text-[#8A9A86]">No sub-admin activity recorded yet.</p>
          ) : (
            <table className="w-full text-sm" data-testid="activity-table">
              <thead className="bg-[#F0EFEA] text-left text-[#1E3F33]"><tr>
                <th className="px-4 py-2.5 font-medium">When</th>
                <th className="px-4 py-2.5 font-medium">Admin</th>
                <th className="px-4 py-2.5 font-medium">Action</th>
                <th className="px-4 py-2.5 font-medium">Section</th>
                <th className="px-4 py-2.5 font-medium">Details</th>
              </tr></thead>
              <tbody>
                {activity.map((r) => {
                  const f = features.find((x) => x.key === r.feature);
                  return (
                    <tr key={r.id} className="border-t border-[#1E3F33]/8" data-testid={`activity-row-${r.id}`}>
                      <td className="px-4 py-2.5 text-[#5C6B64] whitespace-nowrap">{fmtDT(r.created_at)}</td>
                      <td className="px-4 py-2.5 text-[#1E3F33]">{r.actor_username}</td>
                      <td className="px-4 py-2.5">
                        {r.action === "change" ? (
                          <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-[#CC5A3A]/12 text-[#CC5A3A] inline-flex items-center gap-1"><Edit3 className="w-3 h-3" /> Changed</span>
                        ) : (
                          <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-[#2D6A4F]/12 text-[#2D6A4F] inline-flex items-center gap-1"><Eye className="w-3 h-3" /> Viewed</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-[#5C6B64]">{f ? f.label : r.feature}</td>
                      <td className="px-4 py-2.5 text-[#8A9A86] text-xs">{r.detail ? <span className="text-[#5C6B64]">{r.detail}</span> : <span className="font-mono">{r.method} {r.path}</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>  <DialogContent className="max-w-2xl" data-testid="create-admin-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-[#2D6A4F]" /> New admin account</DialogTitle>
            <DialogDescription>Set login credentials and choose what this admin can access. Share the temporary password with them — they can change it after logging in.</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#5C6B64]">Username</label>
              <input data-testid="new-admin-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="input-soft mt-1" placeholder="e.g. janeadmin" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#5C6B64]">Temporary password</label>
              <input data-testid="new-admin-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="input-soft mt-1" placeholder="Min 8 chars, mixed case + number" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#5C6B64]">Name (optional)</label>
              <input data-testid="new-admin-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-soft mt-1" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#5C6B64]">Email (optional)</label>
              <input data-testid="new-admin-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-soft mt-1" />
            </div>
          </div>
          <div className="mt-1">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#5C6B64] mb-2">Section permissions</div>
            <PermissionEditor features={features} perms={formPerms} setPerms={setFormPerms} />
          </div>
          <DialogFooter>
            <button onClick={() => setCreateOpen(false)} className="text-sm py-2 px-4 rounded-full border border-[#1E3F33]/20 text-[#1E3F33] hover:bg-[#1E3F33]/5">Cancel</button>
            <button onClick={submitCreate} disabled={saving || !form.username || !form.password} data-testid="create-admin-submit" className="text-sm py-2 px-4 inline-flex items-center gap-2 rounded-full bg-[#2D6A4F] text-white hover:bg-[#245741] disabled:opacity-40">
              <CheckCircle2 className="w-4 h-4" /> {saving ? "Creating…" : "Create admin"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit permissions dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => { if (!o) setEditTarget(null); }}>
        <DialogContent className="max-w-2xl" data-testid="edit-admin-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Pencil className="w-5 h-5 text-[#2D6A4F]" /> Edit “{editTarget?.username}”</DialogTitle>
            <DialogDescription>Adjust the display name and section-by-section access. Changes take effect immediately.</DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#5C6B64]">Name</label>
            <input data-testid="edit-admin-name" value={editName} onChange={(e) => setEditName(e.target.value)} className="input-soft mt-1 mb-3" />
            <div className="text-xs font-semibold uppercase tracking-wider text-[#5C6B64] mb-2">Section permissions</div>
            <PermissionEditor features={features} perms={editPerms} setPerms={setEditPerms} />
          </div>
          <DialogFooter>
            <button onClick={() => setEditTarget(null)} className="text-sm py-2 px-4 rounded-full border border-[#1E3F33]/20 text-[#1E3F33] hover:bg-[#1E3F33]/5">Cancel</button>
            <button onClick={submitEdit} disabled={saving} data-testid="edit-admin-submit" className="text-sm py-2 px-4 inline-flex items-center gap-2 rounded-full bg-[#2D6A4F] text-white hover:bg-[#245741] disabled:opacity-40">
              <CheckCircle2 className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!pwdTarget} onOpenChange={(o) => { if (!o) { setPwdTarget(null); setNewPwd(""); } }}>
        <DialogContent data-testid="reset-pwd-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-[#2D6A4F]" /> Reset password</DialogTitle>
            <DialogDescription>Set a new password for “{pwdTarget?.username}”. Share it with them securely — they can change it after signing in.</DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#5C6B64]">New password</label>
            <input data-testid="reset-pwd-input" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} className="input-soft mt-1" placeholder="Min 8 chars, mixed case + number" />
          </div>
          <DialogFooter>
            <button onClick={() => { setPwdTarget(null); setNewPwd(""); }} className="text-sm py-2 px-4 rounded-full border border-[#1E3F33]/20 text-[#1E3F33] hover:bg-[#1E3F33]/5">Cancel</button>
            <button onClick={submitResetPwd} disabled={saving || !newPwd} data-testid="reset-pwd-submit" className="text-sm py-2 px-4 inline-flex items-center gap-2 rounded-full bg-[#2D6A4F] text-white hover:bg-[#245741] disabled:opacity-40">
              <CheckCircle2 className="w-4 h-4" /> {saving ? "Saving…" : "Set password"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
